import { WEAPON_SEQUENCE, WeaponType } from '../utils/constants';
import { getWeaponCeilingForLevel } from './weaponBalance';

export const RUN_GRADE_ACCURACY = {
  s: 0.055,
  a: 0.025,
} as const;

export type RunGrade = 'S' | 'A' | 'B' | '-';

export interface RunPerformanceInput {
  level: number;
  isVictory: boolean;
  finaleDefeated: boolean;
  shieldRemaining: number;
  shieldMax: number;
  leaks: number;
  shotsFired: number;
  shotsHit: number;
  weapon: WeaponType;
}

export interface RunPerformance {
  grade: RunGrade;
  accuracy: number;
  shieldRatio: number;
  intendedWeapon: WeaponType;
}

export function getIntendedWeaponForLevel(level: number): WeaponType {
  return getWeaponCeilingForLevel(level);
}

export function calculateRunPerformance(input: RunPerformanceInput): RunPerformance {
  const accuracy = input.shotsFired > 0
    ? Math.max(0, Math.min(1, input.shotsHit / input.shotsFired))
    : 0;
  const shieldRatio = input.shieldMax > 0
    ? Math.max(0, Math.min(1, input.shieldRemaining / input.shieldMax))
    : 0;
  const intendedWeapon = getIntendedWeaponForLevel(input.level);

  if (!input.isVictory || !input.finaleDefeated) {
    return { grade: '-', accuracy, shieldRatio, intendedWeapon };
  }

  const weaponRank = WEAPON_SEQUENCE.indexOf(input.weapon);
  const intendedWeaponRank = WEAPON_SEQUENCE.indexOf(intendedWeapon);
  const reachedIntendedWeapon = weaponRank >= intendedWeaponRank;
  const reachedPreviousWeapon = weaponRank >= Math.max(0, intendedWeaponRank - 1);

  if (
    input.leaks === 0 &&
    shieldRatio >= 0.8 &&
    accuracy >= RUN_GRADE_ACCURACY.s &&
    reachedIntendedWeapon
  ) {
    return { grade: 'S', accuracy, shieldRatio, intendedWeapon };
  }

  if (
    shieldRatio >= 0.4 &&
    input.leaks <= Math.ceil(input.shieldMax * 0.4) &&
    accuracy >= RUN_GRADE_ACCURACY.a &&
    reachedPreviousWeapon
  ) {
    return { grade: 'A', accuracy, shieldRatio, intendedWeapon };
  }

  return { grade: 'B', accuracy, shieldRatio, intendedWeapon };
}
